import fs from "node:fs/promises";
import SCRIPT_WORKFLOWS_BINDING from "worker:workflows/binding";
import SCRIPT_WORKFLOWS_WRAPPED_BINDING from "worker:workflows/wrapped-binding";
import { z } from "zod";
import {
	getUserServiceName,
	OBSERVABILITY_COLLECTOR_SERVICE_NAME,
	OBSERVABILITY_COMPAT_FLAGS,
} from "../core";
import {
	getPersistPath,
	getUserBindingServiceName,
	ProxyNodeBinding,
	SERVICE_DEV_REGISTRY_PROXY,
} from "../shared";
import type { Service } from "../../runtime";
import type { Plugin, RemoteProxyConnectionString } from "../shared";

export const WorkflowsOptionsSchema = z.object({
	workflows: z
		.record(
			z.string(),
			z.object({
				name: z.string(),
				className: z.string(),
				scriptName: z.string().optional(),
				// When set, the workflow's `scriptName` refers to a worker that lives
				// outside this Miniflare instance (registered in the wrangler dev
				// registry). The engine's USER_WORKFLOW binding is rerouted through
				// the dev-registry-proxy so calls reach the external worker. Set by
				// `getExternalServiceEntrypoints` in `src/index.ts`; not part of the
				// public API.
				external: z.boolean().optional(),
				remoteProxyConnectionString: z
					.custom<RemoteProxyConnectionString>()
					.optional(),
				stepLimit: z.number().int().min(1).optional(),
				compatibilityFlags: z.string().array().optional(),
			})
		)
		.optional(),
});
export const WorkflowsSharedOptionsSchema = z.object({
	// Shared with the core plugin; lets us tail the engine service (see below).
	unsafeObservability: z.boolean().optional(),
});

export const WORKFLOWS_PLUGIN_NAME = "workflows";
export const WORKFLOWS_STORAGE_SERVICE_NAME = `${WORKFLOWS_PLUGIN_NAME}:storage`;

export const WORKFLOWS_PLUGIN: Plugin<
	typeof WorkflowsOptionsSchema,
	typeof WorkflowsSharedOptionsSchema
> = {
	options: WorkflowsOptionsSchema,
	sharedOptions: WorkflowsSharedOptionsSchema,
	bindingTypeDescription: "Workflow",
	async getBindings(options: z.infer<typeof WorkflowsOptionsSchema>) {
		return Object.entries(options.workflows ?? {}).map(
			([bindingName, workflow]) => ({
				name: bindingName,
				wrapped: {
					moduleName: `${WORKFLOWS_PLUGIN_NAME}:local-wrapped-binding`,
					innerBindings: [
						{
							name: "binding",
							service: {
								name: getUserBindingServiceName(
									WORKFLOWS_PLUGIN_NAME,
									workflow.name,
									workflow.remoteProxyConnectionString
								),
								entrypoint: "WorkflowBinding",
							},
						},
					],
				},
			})
		);
	},

	async getNodeBindings(options) {
		return Object.fromEntries(
			Object.keys(options.workflows ?? {}).map((bindingName) => [
				bindingName,
				new ProxyNodeBinding(),
			])
		);
	},

	getExtensions() {
		return [
			{
				modules: [
					{
						name: `${WORKFLOWS_PLUGIN_NAME}:local-wrapped-binding`,
						esModule: SCRIPT_WORKFLOWS_WRAPPED_BINDING(),
						internal: true,
					},
				],
			},
		];
	},

	async getServices({
		options,
		tmpPath,
		resourcePersistencePath,
		sharedOptions,
	}) {
		const persistPath = getPersistPath(
			WORKFLOWS_PLUGIN_NAME,
			tmpPath,
			resourcePersistencePath
		);
		await fs.mkdir(persistPath, { recursive: true });
		// each workflow should get its own storage service
		const storageServices: Service[] = Object.entries(
			options.workflows ?? {}
		).map<Service>(([_, workflow]) => ({
			name: `${WORKFLOWS_STORAGE_SERVICE_NAME}-${workflow.name}`,
			disk: { path: persistPath, writable: true },
		}));

		// The engine service is built here, not through the core plugin's
		// per-user-worker path, so tail it explicitly or workflow invocations are
		// invisible in the Local Explorer.
		const observabilityEnabled = sharedOptions.unsafeObservability === true;

		// this creates one miniflare service per workflow that the user's script has. we should dedupe engine definition later
		const services = Object.entries(options.workflows ?? {}).map<Service>(
			([bindingName, workflow]) => {
				// NOTE(lduarte): the engine unique namespace key must be unique per workflow definition
				// otherwise workerd will crash because there's two equal DO namespaces
				const uniqueKey = `miniflare-workflows-${workflow.name}`;

				const engineCompatibilityFlags = [
					"experimental",
					...(workflow.compatibilityFlags ?? []),
				];
				// Mirrors core's designator shape (prefixed name, JSON props);
				// attributes the engine's invocations to the workflow.
				const streamingTails = observabilityEnabled
					? [
							{
								name: getUserServiceName(OBSERVABILITY_COLLECTOR_SERVICE_NAME),
								props: { json: JSON.stringify({ worker: workflow.name }) },
							},
						]
					: undefined;
				if (observabilityEnabled) {
					engineCompatibilityFlags.push(
						...OBSERVABILITY_COMPAT_FLAGS.filter(
							(flag) => !engineCompatibilityFlags.includes(flag)
						)
					);
				}

				const workflowsBinding: Service = {
					name: getUserBindingServiceName(
						WORKFLOWS_PLUGIN_NAME,
						workflow.name,
						workflow.remoteProxyConnectionString
					),
					worker: {
						compatibilityDate: "2024-10-22",
						compatibilityFlags: Array.from(new Set(engineCompatibilityFlags)),
						...(streamingTails ? { streamingTails } : {}),
						modules: [
							{
								name: "workflows.mjs",
								esModule: SCRIPT_WORKFLOWS_BINDING(),
							},
						],
						durableObjectNamespaces: [
							{
								className: "Engine",
								enableSql: true,
								uniqueKey,
								preventEviction: true,
							},
						],
						durableObjectStorage: {
							localDisk: `${WORKFLOWS_STORAGE_SERVICE_NAME}-${workflow.name}`,
						},
						bindings: [
							{
								name: "ENGINE",
								durableObjectNamespace: { className: "Engine" },
							},
							workflow.external && workflow.scriptName
								? {
										name: "USER_WORKFLOW",
										service: {
											name: getUserServiceName(SERVICE_DEV_REGISTRY_PROXY),
											entrypoint: "ExternalServiceProxy",
											props: {
												json: JSON.stringify({
													service: workflow.scriptName,
													entrypoint: workflow.className,
												}),
											},
										},
									}
								: {
										name: "USER_WORKFLOW",
										service: {
											name: getUserServiceName(workflow.scriptName),
											entrypoint: workflow.className,
										},
									},
							{
								name: "BINDING_NAME",
								json: JSON.stringify(bindingName),
							},
							{
								name: "WORKFLOW_NAME",
								json: JSON.stringify(workflow.name),
							},
							...(workflow.stepLimit !== undefined
								? [
										{
											name: "STEP_LIMIT",
											json: JSON.stringify(workflow.stepLimit),
										},
									]
								: []),
						],
					},
				};

				return workflowsBinding;
			}
		);

		if (services.length === 0) {
			return [];
		}

		return [...storageServices, ...services];
	},
};
